import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import useFlags from '@/hooks/useFlags';
import cn from '@/utils/cn';
import SPAButton from '../../shared/SPAButton';
import SPAPill from '../../shared/SPAPill';
import SPAPageHeader from '../../shared/SPAPageHeader';
import CollapsibleSection, { sectionCardClass, sectionTitleClass } from './CollapsibleSection';
import ImpersonateButton from '../../shared/ImpersonateButton';
import LinkedAccountsSection from './LinkedAccountsSection';
import SavedWorkSection from './SavedWorkSection';
import TransactionResults, { linkClass } from './TransactionResults';
import { formatDateTime } from './format';
import { useStafferSavedWork, useUserDetail } from './hooks';
import { recordRecentLookup } from './recentLookups';
import rolePillTone from './rolePillTone';
import type { UserSearchDetail, UserType } from './types';

const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="tw-min-w-0">
    <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">{label}</p>
    <p className="tw-m-0 tw-break-words tw-text-sm tw-text-spa-primary">{value}</p>
  </div>
);

// A raised card with the same title scale as the collapsible sections below it.
const SectionCard = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <section className={cn(sectionCardClass, 'tw-px-5 tw-py-4')}>
    <h3 className={cn(sectionTitleClass, 'tw-mb-3')}>{title}</h3>
    {children}
  </section>
);

const SECTION_KEYS = ['linked-accounts', 'saved-work', 'transactions'] as const;
type SectionKey = typeof SECTION_KEYS[number];

const sectionFromHash = (hash: string) => SECTION_KEYS.find(key => `#${key}` === hash);

interface GlanceLinkProps {
  href: string;
  label: string;
  loading?: boolean;
  muted: boolean;
  value: string;
  onClick?: () => void;
}

// A raised tile linking down to its section; the value is the section's headline number.
const GlanceLink = ({
  href, label, loading, muted, value, onClick,
}: GlanceLinkProps) => (
  <a
    className={cn(
      'tw-box-border tw-flex tw-min-w-[7rem] tw-flex-col tw-gap-0.5 tw-rounded-lg tw-border tw-border-solid',
      'tw-border-spa-border-secondary tw-bg-spa-bg-secondary tw-px-3 tw-py-2 tw-no-underline',
      'hover:tw-border-spa-border-hover hover:tw-no-underline',
    )}
    href={href}
    onClick={onClick}
  >
    <span className="tw-text-sm tw-text-spa-secondary">{label}</span>
    {loading ? (
      <span
        aria-hidden="true"
        className="tw-my-1 tw-h-4 tw-w-10 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton"
        data-testid="glance-skeleton"
      />
    ) : (
      <span className={cn('tw-text-base tw-font-semibold', muted ? 'tw-text-spa-tertiary' : 'tw-text-spa-heading')}>
        {value}
      </span>
    )}
  </a>
);

const AtAGlance = ({ detail, onExpand }: {
  detail: UserSearchDetail;
  onExpand: (section: SectionKey) => void;
}) => {
  const savedWork = useStafferSavedWork(detail.id, true);

  const savedTotal = savedWork.data?.types.reduce((sum, type) => sum + type.total, 0);
  const sharedTotal = savedWork.data?.sharing.reduce(
    (sum, summary) => sum + summary.district + summary.everyone, 0,
  );
  const linked = [
    detail.provider && 'Rostering',
    detail.google && 'Google',
    detail.lti_accounts.length > 0 && 'LMS',
  ].filter(Boolean).join(', ');
  const orders = `${detail.transactions.length}${detail.transactions_truncated ? '+' : ''}`;

  return (
    <div className="tw-flex tw-flex-wrap tw-gap-3">
      <GlanceLink
        href="#linked-accounts"
        label="Linked accounts"
        muted={!linked}
        value={linked || 'None'}
        onClick={() => onExpand('linked-accounts')}
      />
      <GlanceLink
        href="#saved-work"
        label="Saved work"
        loading={savedWork.isLoading}
        muted={savedTotal === 0}
        value={savedTotal === undefined ? '—' : String(savedTotal)}
        onClick={() => onExpand('saved-work')}
      />
      <GlanceLink
        href="#saved-work"
        label="Shared to banks"
        loading={savedWork.isLoading}
        muted={sharedTotal === 0}
        value={sharedTotal === undefined ? '—' : String(sharedTotal)}
        onClick={() => onExpand('saved-work')}
      />
      <GlanceLink
        href="#transactions"
        label="Transactions"
        muted={detail.transactions.length === 0}
        value={orders}
        onClick={() => onExpand('transactions')}
      />
    </div>
  );
};

const DetailSkeleton = () => (
  <div aria-busy="true" data-testid="detail-skeleton">
    <div className="tw-h-8 tw-w-64 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton" />
    <div className="tw-mt-5 tw-flex tw-gap-3">
      {[0, 1, 2, 3].map(tile => (
        <div key={tile} className="tw-h-14 tw-w-28 tw-animate-pulse tw-rounded-lg tw-bg-spa-bg-skeleton-subtle" />
      ))}
    </div>
    <div className="tw-mt-6 tw-h-40 tw-animate-pulse tw-rounded-lg tw-bg-spa-bg-skeleton-subtle" />
    <div className="tw-mt-6 tw-h-20 tw-animate-pulse tw-rounded-lg tw-bg-spa-bg-skeleton-subtle" />
  </div>
);

interface UserDetailPanelProps {
  id: string;
  userType: UserType;
}

const UserDetailPanel = ({ id, userType }: UserDetailPanelProps) => {
  const { accessibilityReflow } = useFlags();
  const detailQuery = useUserDetail(userType, id);
  const loaded = detailQuery.data;
  const initialSection = sectionFromHash(useLocation().hash);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    'linked-accounts': initialSection === 'linked-accounts',
    'saved-work': initialSection === 'saved-work',
    transactions: initialSection === 'transactions',
  });

  const setSectionOpen = (section: SectionKey) => (open: boolean) => (
    setOpenSections(previous => ({ ...previous, [section]: open }))
  );

  useEffect(() => {
    if (!loaded) return;

    recordRecentLookup({
      id: loaded.id,
      name: [loaded.first_name, loaded.last_name].filter(Boolean).join(' ') || `User ${loaded.id}`,
      role: loaded.role,
      user_type: loaded.user_type,
    });
  }, [loaded]);

  if (detailQuery.isLoading) return <DetailSkeleton />;

  if (detailQuery.isError) {
    return (
      <p className="tw-m-0 tw-text-sm tw-text-spa-error">
        Could not load this user — the account may not exist or may be an internal account.
      </p>
    );
  }

  const detail = detailQuery.data;
  const linkedCount = [detail.provider, detail.google, detail.lti_accounts.length > 0]
    .filter(Boolean).length;
  const manageHref = detail.subscriber && (detail.user_type === 'student'
    ? Routes.admin_subscriber_students_path(detail.subscriber.id)
    : Routes.admin_subscriber_teachers_path(detail.subscriber.id));

  return (
    <div>
      <SPAPageHeader
        actions={(
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <ImpersonateButton
              id={detail.id}
              name={[detail.first_name, detail.last_name].filter(Boolean).join(' ') || null}
              userType={detail.user_type}
            />
            {manageHref && (
              <SPAButton asChild variant="outline">
                <a className="tw-whitespace-nowrap" href={manageHref}>Manage in subscriber</a>
              </SPAButton>
            )}
          </div>
        )}
        title={(
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {detail.first_name} {detail.last_name}
            <SPAPill className="tw-capitalize" tone={rolePillTone(detail.role)}>{detail.role}</SPAPill>
            {!detail.active && <SPAPill>Inactive</SPAPill>}
            {detail.locked && <SPAPill tone="error">Locked</SPAPill>}
          </span>
        )}
      />

      <div className="tw-flex tw-flex-col tw-gap-5">
        {detail.user_type === 'staffer' && (
          <AtAGlance detail={detail} onExpand={section => setSectionOpen(section)(true)} />
        )}

        <SectionCard title="Account">
          <div
            className={accessibilityReflow
              ? 'tw-grid tw-grid-cols-[repeat(auto-fit,minmax(min(18rem,100%),1fr))] tw-gap-4'
              : 'tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-3'}
          >
            {detail.email && <DetailItem label="Email" value={detail.email} />}
            <DetailItem label="Username" value={detail.username || '—'} />
            <DetailItem label="User ID" value={detail.id} />
            <DetailItem label="Created" value={formatDateTime(detail.created_at)} />
            <DetailItem label="Last sign-in" value={formatDateTime(detail.current_sign_in_at)} />
            <DetailItem label="Sign-in count" value={detail.sign_in_count} />
          </div>
        </SectionCard>

        {detail.subscriber && (
          <SectionCard title="Subscriber">
            <p className="tw-m-0 tw-text-sm">
              <a
                className={cn(linkClass, 'tw-font-semibold')}
                href={Routes.admin_subscriber_home_path(detail.subscriber.id)}
              >
                {detail.subscriber.name}
              </a>
              <span className="tw-ml-2 tw-text-spa-secondary">
                {[
                  detail.subscriber.customer_number && `Customer #${detail.subscriber.customer_number}`,
                  [detail.subscriber.city, detail.subscriber.state].filter(Boolean).join(', '),
                ].filter(Boolean).join(' · ')}
              </span>
            </p>
          </SectionCard>
        )}

        <CollapsibleSection
          headerExtra={detail.google_conflicts.length > 0 && (
            <SPAPill tone="warning">Google conflict</SPAPill>
          )}
          id="linked-accounts"
          open={openSections['linked-accounts']}
          summary={linkedCount === 0 ? '(none linked)' : `(${linkedCount} linked)`}
          title="Linked accounts"
          onOpenChange={setSectionOpen('linked-accounts')}
        >
          <LinkedAccountsSection detail={detail} />
        </CollapsibleSection>

        {detail.user_type === 'staffer' && (
          <>
            <SavedWorkSection
              open={openSections['saved-work']}
              stafferId={detail.id}
              onOpenChange={setSectionOpen('saved-work')}
            />
            <TransactionResults
              limit={detail.transactions.length}
              open={openSections.transactions}
              transactions={detail.transactions}
              truncated={detail.transactions_truncated}
              onOpenChange={setSectionOpen('transactions')}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default UserDetailPanel;
